import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../../components/ui/dialog';
import {
    Alert,
    AlertDescription,
    AlertTitle,
} from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
    RefreshCw, 
    Edit2, 
    X, 
    AlertTriangle, 
    CheckCircle,
    Clock,
    ArrowLeft,
    TrendingUp,
    Info
} from 'lucide-react';
import axios from 'axios';

import { API_BASE } from '../../lib/apiBase';
const API = `${API_BASE}/api`;

// Currency display names and symbols
const CURRENCY_INFO = {
    EUR: { name: 'Euro', symbol: '€', flag: '🇪🇺' },
    DKK: { name: 'Danish Krone', symbol: 'kr', flag: '🇩🇰' },
    SEK: { name: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪' },
    NOK: { name: 'Norwegian Krone', symbol: 'kr', flag: '🇳🇴' },
    GBP: { name: 'British Pound', symbol: '£', flag: '🇬🇧' },
    USD: { name: 'US Dollar', symbol: '$', flag: '🇺🇸' }
};

// Scandinavian currencies that use special rounding
const SCANDINAVIAN_CURRENCIES = ['DKK', 'SEK', 'NOK'];

export default function ExchangeRatesPage({ embedded = false }) {
    const { token } = useAuth();
    const navigate = useNavigate();
    
    const [ratesData, setRatesData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(null);
    
    // Override modal state
    const [overrideModal, setOverrideModal] = useState({ open: false, currency: null });
    const [overrideRate, setOverrideRate] = useState('');
    const [overrideSubmitting, setOverrideSubmitting] = useState(false);
    
    // Clear override confirmation
    const [clearModal, setClearModal] = useState({ open: false, currency: null });
    const [clearSubmitting, setClearSubmitting] = useState(false);

    const fetchRates = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/admin/exchange-rates`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRatesData(response.data);
            setError(null);
        } catch (err) {
            console.error('Failed to fetch exchange rates:', err);
            setError('Failed to load exchange rates');
            toast.error('Failed to load exchange rates');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRates();
    }, [token]);

    const handleFetchFromECB = async () => {
        try {
            setFetching(true);
            const response = await axios.post(`${API}/admin/exchange-rates/fetch`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                toast.success(`Fetched ${response.data.rates_updated} rates from ECB (${response.data.ecb_date})`);
                fetchRates();
            } else {
                toast.error(response.data.error || 'Failed to fetch rates');
            }
        } catch (err) {
            console.error('Failed to fetch from ECB:', err);
            toast.error(err.response?.data?.detail || 'Failed to fetch rates from ECB');
        } finally {
            setFetching(false);
        }
    };

    const handleOpenOverride = (currency, currentRate) => {
        setOverrideRate(currentRate?.toString() || '');
        setOverrideModal({ open: true, currency });
    };

    const handleSubmitOverride = async () => {
        const rate = parseFloat(overrideRate);
        if (isNaN(rate) || rate <= 0) {
            toast.error('Please enter a valid positive rate');
            return;
        }
        
        try {
            setOverrideSubmitting(true);
            await axios.put(
                `${API}/admin/exchange-rates/${overrideModal.currency}`,
                { rate },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(`Manual override set for ${overrideModal.currency}`);
            setOverrideModal({ open: false, currency: null });
            fetchRates();
        } catch (err) {
            console.error('Failed to set override:', err);
            toast.error(err.response?.data?.detail || 'Failed to set override');
        } finally {
            setOverrideSubmitting(false);
        }
    };

    const handleClearOverride = async () => {
        try {
            setClearSubmitting(true);
            await axios.delete(
                `${API}/admin/exchange-rates/${clearModal.currency}/override`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            toast.success(`Override cleared for ${clearModal.currency}`);
            setClearModal({ open: false, currency: null });
            fetchRates();
        } catch (err) {
            console.error('Failed to clear override:', err);
            toast.error(err.response?.data?.detail || 'Failed to clear override');
        } finally {
            setClearSubmitting(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Never';
        const date = new Date(dateStr);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'success':
                return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Last fetch successful</Badge>;
            case 'failed':
                return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Last fetch failed</Badge>;
            default:
                return <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">Never fetched</Badge>;
        }
    };

    if (loading) {
        if (embedded) {
            return (
                <div className="flex items-center justify-center py-24">
                    <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                </div>
            );
        }
        return (
            <div className="min-h-screen bg-slate-50">
                <Header />
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
                    <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
                </div>
            </div>
        );
    }

    return (
        <div className={embedded ? '' : 'min-h-screen bg-slate-50'}>
            {!embedded && <Header />}
            
            <main className={embedded ? '' : 'max-w-5xl mx-auto px-6 py-8'}>
                {/* Header */}
                {!embedded && (
                    <div className="flex items-center gap-4 mb-6">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(-1)}
                            className="text-slate-600"
                        >
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                    </div>
                )}

                <div className="flex items-start justify-between mb-8">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900" data-testid="exchange-rates-title">
                            Exchange Rates
                        </h1>
                        <p className="text-slate-500 mt-1">
                            Manage currency exchange rates for customer pricing. Base currency: EUR
                        </p>
                    </div>
                    <Button
                        onClick={handleFetchFromECB}
                        disabled={fetching}
                        className="bg-indigo-600 hover:bg-indigo-700"
                        data-testid="fetch-ecb-btn"
                    >
                        {fetching ? (
                            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <RefreshCw className="h-4 w-4 mr-2" />
                        )}
                        Fetch from ECB
                    </Button>
                </div>

                {/* Status Card */}
                <Card className="mb-6">
                    <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Clock className="h-4 w-4 text-slate-400" />
                                    <span className="text-sm text-slate-600">
                                        Last fetch: {formatDate(ratesData?.last_fetch_at)}
                                    </span>
                                </div>
                                {ratesData?.ecb_date && (
                                    <div className="flex items-center gap-2">
                                        <TrendingUp className="h-4 w-4 text-slate-400" />
                                        <span className="text-sm text-slate-600">
                                            ECB date: {ratesData.ecb_date}
                                        </span>
                                    </div>
                                )}
                            </div>
                            {getStatusBadge(ratesData?.last_fetch_status)}
                        </div>
                        
                        {ratesData?.last_fetch_error && (
                            <Alert variant="destructive" className="mt-4">
                                <AlertTriangle className="h-4 w-4" />
                                <AlertTitle>Fetch Error</AlertTitle>
                                <AlertDescription>{ratesData.last_fetch_error}</AlertDescription>
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                {/* Info about rounding */}
                <Alert className="mb-6 bg-blue-50 border-blue-200">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertTitle className="text-blue-800">Scandinavian Rounding</AlertTitle>
                    <AlertDescription className="text-blue-700">
                        DKK, SEK, and NOK amounts are automatically rounded to the nearest 0.05 for customer-facing prices.
                    </AlertDescription>
                </Alert>

                {/* Rates Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>Currency Rates</CardTitle>
                        <CardDescription>
                            All rates are based on EUR = 1.00. Manual overrides take precedence over ECB rates.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-[200px]">Currency</TableHead>
                                    <TableHead className="text-right">Active Rate</TableHead>
                                    <TableHead className="text-right">ECB Rate</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {/* EUR is always 1.0 */}
                                <TableRow className="bg-slate-50">
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <span className="text-lg">{CURRENCY_INFO.EUR.flag}</span>
                                            <div>
                                                <span className="font-medium">EUR</span>
                                                <span className="text-slate-500 text-sm ml-2">{CURRENCY_INFO.EUR.name}</span>
                                            </div>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right font-mono">1.0000</TableCell>
                                    <TableCell className="text-right font-mono text-slate-400">—</TableCell>
                                    <TableCell>
                                        <Badge variant="secondary">Base Currency</Badge>
                                    </TableCell>
                                    <TableCell className="text-right">—</TableCell>
                                </TableRow>
                                
                                {ratesData?.rates?.map((rate) => (
                                    <TableRow key={rate.currency} data-testid={`rate-row-${rate.currency}`}>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <span className="text-lg">{CURRENCY_INFO[rate.currency]?.flag || ''}</span>
                                                <div>
                                                    <span className="font-medium">{rate.currency}</span>
                                                    <span className="text-slate-500 text-sm ml-2">
                                                        {CURRENCY_INFO[rate.currency]?.name || rate.currency}
                                                    </span>
                                                    {SCANDINAVIAN_CURRENCIES.includes(rate.currency) && (
                                                        <Badge variant="outline" className="ml-2 text-xs bg-blue-50 text-blue-600 border-blue-200">
                                                            ↑ 0.5
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <span className={`font-mono ${rate.is_manual_override ? 'text-amber-600 font-semibold' : ''}`}>
                                                {rate.rate?.toFixed(4) || '—'}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-slate-400">
                                            {rate.last_ecb_rate?.toFixed(4) || '—'}
                                        </TableCell>
                                        <TableCell>
                                            {rate.is_manual_override ? (
                                                <div>
                                                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                                        Manual Override
                                                    </Badge>
                                                    {rate.manual_override_by && (
                                                        <p className="text-xs text-slate-500 mt-1">
                                                            by {rate.manual_override_by}
                                                        </p>
                                                    )}
                                                </div>
                                            ) : rate.rate ? (
                                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    <CheckCircle className="h-3 w-3 mr-1" />
                                                    ECB Rate
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="bg-slate-100 text-slate-600">
                                                    No Rate
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleOpenOverride(rate.currency, rate.rate)}
                                                    data-testid={`override-btn-${rate.currency}`}
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </Button>
                                                {rate.is_manual_override && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => setClearModal({ open: true, currency: rate.currency })}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        data-testid={`clear-override-btn-${rate.currency}`}
                                                    >
                                                        <X className="h-4 w-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </main>

            {/* Override Modal */}
            <Dialog open={overrideModal.open} onOpenChange={(open) => !open && setOverrideModal({ open: false, currency: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Set Manual Override for {overrideModal.currency}</DialogTitle>
                        <DialogDescription>
                            This rate will be used instead of the ECB rate until you clear the override.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Exchange Rate (EUR base)</Label>
                            <div className="flex items-center gap-2">
                                <span className="text-slate-500">1 EUR =</span>
                                <Input
                                    type="number"
                                    step="0.0001"
                                    min="0.0001"
                                    value={overrideRate}
                                    onChange={(e) => setOverrideRate(e.target.value)}
                                    placeholder="e.g. 7.4600"
                                    className="w-32 font-mono"
                                    data-testid="override-rate-input"
                                />
                                <span className="text-slate-500">{overrideModal.currency}</span>
                            </div>
                        </div>
                        {overrideModal.currency && SCANDINAVIAN_CURRENCIES.includes(overrideModal.currency) && (
                            <Alert className="bg-blue-50 border-blue-200">
                                <Info className="h-4 w-4 text-blue-600" />
                                <AlertDescription className="text-blue-700 text-sm">
                                    Note: {overrideModal.currency} amounts will be rounded to the nearest 0.05 for customers.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setOverrideModal({ open: false, currency: null })}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSubmitOverride}
                            disabled={overrideSubmitting}
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid="submit-override-btn"
                        >
                            {overrideSubmitting ? 'Saving...' : 'Set Override'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Clear Override Confirmation */}
            <Dialog open={clearModal.open} onOpenChange={(open) => !open && setClearModal({ open: false, currency: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Clear Manual Override</DialogTitle>
                        <DialogDescription>
                            This will revert {clearModal.currency} to use the ECB rate. Are you sure?
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setClearModal({ open: false, currency: null })}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleClearOverride}
                            disabled={clearSubmitting}
                            variant="destructive"
                            data-testid="confirm-clear-override-btn"
                        >
                            {clearSubmitting ? 'Clearing...' : 'Clear Override'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
